/**
 * useToast Hook + ToastProvider
 *
 * Global toast notification system. Wrap the app in <ToastProvider> (done in
 * root layout), then call `useToast()` from any client component.
 *
 * API:
 *   const { toast, dismiss, dismissAll } = useToast()
 *
 *   toast({ title: 'Done', variant: 'success' })
 *   toast({ title: 'Error', description: 'Details...', variant: 'error' })
 *   toast({ title: 'Warning', variant: 'warning', duration: 8000 })
 *   dismiss(id)
 *   dismissAll()
 */

"use client";

import * as React from "react";
import { ToastContainer, type ToastData, type ToastVariant } from "./toast";

/* ─── Context ─── */

interface ToastInput {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  /** Auto-dismiss in ms. Default 5000. 0 = persist until dismissed. */
  duration?: number;
}

interface ToastContextValue {
  toast: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

/* ─── Provider ─── */

const DEFAULT_DURATION = 5000;
const MAX_TOASTS = 5;

let toastCounter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastData[]>([]);
  const timersRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissAll = React.useCallback(() => {
    setToasts([]);
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  const toast = React.useCallback(
    (input: ToastInput): string => {
      const id = `toast-${++toastCounter}-${Date.now()}`;
      const duration = input.duration ?? DEFAULT_DURATION;

      const newToast: ToastData = {
        id,
        title: input.title,
        description: input.description,
        variant: input.variant ?? "default",
        duration,
      };

      setToasts((prev) => {
        // Keep only the latest MAX_TOASTS - 1 to make room for the new one
        const trimmed = prev.length >= MAX_TOASTS ? prev.slice(1) : prev;
        return [...trimmed, newToast];
      });

      // Auto-dismiss after duration (unless 0)
      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const contextValue = React.useMemo(
    () => ({ toast, dismiss, dismissAll }),
    [toast, dismiss, dismissAll]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/* ─── Hook ─── */

export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return context;
}
